import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';

import { TEMPLATE_GALLERY_INPUT_SOURCE } from '@atlaskit/editor-common/analytics/types/insert-events';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { SnippetsPlugin } from '@atlassian/editor-plugin-snippets';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
} from '../../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../../hooks/analytics/useSnippetAnalytics.ts';
import { useIsEditorViewMode } from '../../hooks/panel/useIsEditorViewMode.ts';
import { useInsertSnippet } from '../../hooks/panel/useInsertSnippet.ts';
import { mapConfluenceSnippet } from '../../provider/mapConfluenceSnippet.ts';
import type { Snippet } from '../../types/snippet.ts';
import { reportError } from '../../utils/reportError.ts';
import { SnippetPreview } from '../../preview/SnippetPreview.tsx';
import { SnippetPreviewSkeleton } from '../../preview/skeleton/SnippetPreviewSkeleton.tsx';
import { useSnippetsActions } from '../../store/snippetsStore.ts';
import { useSnippetPopupPreview } from '../preview/SnippetPopupPreviewContext.tsx';
import {
	SNIPPET_PREVIEW_CONTAINER,
	SnippetPreviewContainer,
} from '../preview/SnippetPreviewContainer.tsx';

import { SnippetPanelCard } from './SnippetPanelCard.tsx';
import { messages } from './SnippetPanelItem.i18n.ts';
import { SnippetPanelItemContent } from './SnippetPanelItemContent.tsx';

const EDITOR_PRE_SCALE_WIDTH = 800;

type SnippetPanelItemProps = {
	snippet: Snippet;
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelItem = ({
	snippet,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelItemProps): React.JSX.Element => {
	const { userId } = useSessionData();
	const editorAPI: PublicPluginAPI<SnippetsPlugin> | null | undefined =
		useContext(EditorContextPanelContext).editorAPI;

	const intl = useIntl();

	const [isHovered, setIsHovered] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const isViewMode = useIsEditorViewMode();
	const insertSnippet = useInsertSnippet(editorAPI);
	const snippetsActions = useSnippetsActions();
	const { fireButtonClickedAnalytic, fireFailedAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.SNIPPET_PANEL,
	);
	const { showPreview, hidePreview } = useSnippetPopupPreview();

	const handleMouseEnter = (event: React.MouseEvent) => {
		setIsHovered(true);
		showPreview(snippet, event.currentTarget.getBoundingClientRect());
	};
	const handleMouseLeave = () => setIsHovered(false);
	const handleMenuOpenChange = (isOpen: boolean) => setIsMenuOpen(isOpen);
	const fireInsertSuccess = (): void => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.INSERT_SNIPPET, {
			userId: userId ?? undefined,
			scope: snippet.scope,
			snippetId: snippet.snippetId,
			isShared: snippet.isShared,
			templateType: snippet.templateType,
			timestamp: Date.now(),
		});
	};
	const fireInsertFailure = (reason: string): void => {
		fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_INSERT, {
			userId: userId ?? undefined,
			scope: snippet.scope,
			snippetId: snippet.snippetId,
			isShared: snippet.isShared,
			templateType: snippet.templateType,
			reason,
			timestamp: Date.now(),
		});
	};
	const handleClick = () => {
		if (isViewMode) {
			return;
		}

		if (!expValEquals('platform_editor_snippets_plugin_confluence', 'isEnabled', true)) {
			const result = insertSnippet(snippet, true);
			if (result.status === 'success') {
				hidePreview();
				snippetsActions.recordSnippetInsert(snippet.snippetId);
				fireInsertSuccess();
			} else if (result.status === 'error') {
				fireInsertFailure(result.reason);
			}
			return;
		}

		if (!editorAPI?.snippets?.commands.insertSnippet) {
			return;
		}

		try {
			const platformSnippet = mapConfluenceSnippet(snippet);
			editorAPI.core.actions.execute(
				editorAPI.snippets.commands.insertSnippet({
					snippet: platformSnippet,
					inputSource: TEMPLATE_GALLERY_INPUT_SOURCE,
					onSuccess: () => {
						hidePreview();
						try {
							snippetsActions.recordSnippetInsert(snippet.snippetId);
						} catch (error) {
							reportError(error);
						}
						fireInsertSuccess();
					},
					onError: fireInsertFailure,
				}),
			);
		} catch (error) {
			reportError(error);
			fireInsertFailure('insertError');
		}
	};

	const showActions = isHovered || snippet.isFavorite || isMenuOpen;
	const showPanelActions = isHovered || isMenuOpen;

	return (
		<SnippetPanelCard
			isActive={isMenuOpen}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={handleClick}
			isDisabled={isViewMode}
			ariaLabel={intl.formatMessage(messages.snippetCardAriaLabel, { name: snippet.name })}
			testId={`snippet-card-button-${snippet.snippetId}`}
		>
			<SnippetPreviewContainer>
				<SnippetPreview
					snippet={snippet}
					dimensions={{
						width: `calc(${SNIPPET_PREVIEW_CONTAINER.width}px - (${SNIPPET_PREVIEW_CONTAINER.paddingInline} * 2))`,
						maxHeight: `calc(${SNIPPET_PREVIEW_CONTAINER.height}px - (${SNIPPET_PREVIEW_CONTAINER.paddingBlock} * 2))`,
					}}
					renderWidth={EDITOR_PRE_SCALE_WIDTH}
					loadingComponent={<SnippetPreviewSkeleton />}
				/>
			</SnippetPreviewContainer>
			<SnippetPanelItemContent
				snippet={snippet}
				isHovered={isHovered}
				showActions={showActions}
				showPanelActions={showPanelActions}
				onMenuOpenChange={handleMenuOpenChange}
				contentId={contentId}
				spaceId={spaceId}
				spaceKey={spaceKey}
			/>
		</SnippetPanelCard>
	);
};
